// Network connectivity detection utilities

import StorageService from '../services/storage';

export interface NetworkState {
  isOnline: boolean;
  connectionType: string;
  effectiveType: string;
}

class NetworkDetection {
  private static listeners: Array<(isOnline: boolean) => void> = [];
  
  // Get current network status
  static getNetworkState(): NetworkState {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    return {
      isOnline: navigator.onLine,
      connectionType: connection?.type || 'unknown',
      effectiveType: connection?.effectiveType || 'unknown'
    };
  }
  
  // Check if device is online
  static isOnline(): boolean {
    return navigator.onLine;
  }
  
  // Check if device is offline
  static isOffline(): boolean {
    return !navigator.onLine;
  }
  
  // Add listener for network state changes
  static addListener(callback: (isOnline: boolean) => void): void {
    this.listeners.push(callback);
  }
  
  // Remove listener
  static removeListener(callback: (isOnline: boolean) => void): void {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }
  
  // Initialize network detection with event listeners
  static initialize(): () => void {
    const handleOnline = () => {
      StorageService.setOfflineMode(false);
      this.listeners.forEach(callback => callback(true));
    };
    
    const handleOffline = () => {
      StorageService.setOfflineMode(true);
      this.listeners.forEach(callback => callback(false));
    };
    
    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Set initial state
    StorageService.setOfflineMode(!navigator.onLine);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      this.listeners = [];
    };
  }
  
  // Test network connectivity by making a lightweight request
  static async testConnectivity(timeout: number = 5000): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch('/api/health/', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      return false;
    }
  }
  
  // Get connection quality assessment
  static getConnectionQuality(): 'poor' | 'good' | 'excellent' | 'unknown' {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    if (!connection) return 'unknown';
    
    const effectiveType = connection.effectiveType;
    
    switch (effectiveType) {
      case 'slow-2g':
      case '2g':
        return 'poor';
      case '3g':
        return 'good';
      case '4g':
        return 'excellent';
      default:
        return 'unknown';
    }
  }
  
  // Check if we should retry based on connection quality
  static shouldRetryOnConnection(): boolean {
    const quality = this.getConnectionQuality();
    const isOnline = this.isOnline();
    
    // Don't retry if offline
    if (!isOnline) return false;
    
    // Always retry on good connections
    if (quality === 'good' || quality === 'excellent') return true;
    
    // Be conservative on poor connections
    if (quality === 'poor') return false;
    
    // Default to retry if connection quality is unknown but online
    return true;
  }
}

export default NetworkDetection;